/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useMemo } from 'react';
import { useInitialProjectTemplate } from '@jira/platform__services-project-properties/src/services/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { DROPDOWN_ITEM, BUTTON } from '../../constants/analytics/action-subject-type.tsx';
import {
	CUSTOM_ITEM_DROPDOWN_ACTION,
	CUSTOM_ITEM_BUTTON,
	FIXED_ITEM_DROPDOWN_ACTION,
} from '../../constants/analytics/component-type.tsx';
import { PROJECT, BOARD, CHANGE_VIEW } from '../../constants/analytics/item-type.tsx';
import { MEATBALL_DROPDOWN } from '../../constants/analytics/item-id.tsx';
import { LEVEL_THREE } from '../../constants/analytics/level.tsx';
import type {
	BoardMeatballDropdownMenuItemParams,
	ChangeViewViewOptionParams,
	ProjectMeatballDropdownMenuItemParams,
} from '../types.tsx';
import { getSectionByStarredOrRecent } from '../utils.tsx';
import { PROJECTS_NAVIGATION_ITEM } from '../../constants/analytics/action-subject-id.tsx';

// We can pass an `overrideProjectId` so that we send the proper event data.
// This is needed when you are on Project A and then open the side bar menu in Project B.
// Without this projectId it uses the route projectId which is incorrect in this scenario
export const useProjectsAnalyticsL3 = (
	overrideProjectId?: string,
): {
	sendMeatBallDropDownOnProjectsMenuItemAnalyticsEvent: ({
		itemId,
		recent,
		starred,
		projectId,
		projectType,
	}: ProjectMeatballDropdownMenuItemParams) => void;
	sendMeatBallDropDownOnBoardsAnalyticsEvent: ({
		recent,
		starred,
		boardId,
		projectId,
		projectType,
	}: BoardMeatballDropdownMenuItemParams) => void;
	sendChangeViewViewOptionAnalyticsEvent: ({ itemId }: ChangeViewViewOptionParams) => void;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const initialProjectTemplate = useInitialProjectTemplate(
		fg('initial-project-template-event-attr') ? overrideProjectId : undefined,
	);

	const commonAttributes = useMemo(
		() => ({
			level: LEVEL_THREE,
			initialProjectTemplate,
		}),
		[initialProjectTemplate],
	);

	// actions inside meatball menu on projects
	const sendMeatBallDropDownOnProjectsMenuItemAnalyticsEvent = useCallback(
		({
			itemId,
			recent,
			starred,
			projectId,
			projectType,
		}: ProjectMeatballDropdownMenuItemParams): void => {
			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: DROPDOWN_ITEM,
			});

			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM_DROPDOWN_ACTION,
				itemId,
				parentItemId: projectId,
				parentItemType: PROJECT,
				projectType,
				section: getSectionByStarredOrRecent({ starred, recent }),
			};
			fireUIAnalytics(event, 'projectsNavigationMenuItem', attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	// meatball menu on boards
	const sendMeatBallDropDownOnBoardsAnalyticsEvent = useCallback(
		({
			recent,
			starred,
			boardId,
			projectId,
			projectType,
		}: BoardMeatballDropdownMenuItemParams): void => {
			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: BUTTON,
			});

			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM_BUTTON,
				itemId: MEATBALL_DROPDOWN,
				projectId,
				parentItemId: boardId,
				parentItemType: BOARD,
				projectType,
				section: getSectionByStarredOrRecent({ starred, recent }),
			};
			fireUIAnalytics(event, 'projectsNavigationMenuItem', attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	const sendChangeViewViewOptionAnalyticsEvent = useCallback(
		({ itemId }: ChangeViewViewOptionParams): void => {
			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: DROPDOWN_ITEM,
			});

			const attributes = {
				...commonAttributes,
				componentType: FIXED_ITEM_DROPDOWN_ACTION,
				itemId,
				parentItemId: CHANGE_VIEW,
			};
			fireUIAnalytics(event, PROJECTS_NAVIGATION_ITEM, attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	return {
		sendMeatBallDropDownOnProjectsMenuItemAnalyticsEvent,
		sendMeatBallDropDownOnBoardsAnalyticsEvent,
		sendChangeViewViewOptionAnalyticsEvent,
	};
};
